import React, { memo } from "react";
import {
  View,
  TextInput as RNTextInput,
  type TextInputProps as RNTextInputProps,
} from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Label } from "@/components/ui/Label";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

// ─── Affix helper ─────────────────────────────────────────────────────────────

function AffixBox({
  side,
  children,
}: {
  side: "left" | "right";
  children: string | React.ReactNode;
}) {
  return (
    <View
      className={`items-center justify-center px-3 self-stretch bg-stroke-20 ${
        side === "left" ? "border-e border-stroke-50" : "border-s border-stroke-50"
      }`}
    >
      {typeof children === "string" ? (
        <AppText weight="regular" className="text-base text-dark-text-20">
          {children}
        </AppText>
      ) : (
        children
      )}
    </View>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AppTextInputProps extends Omit<RNTextInputProps, "style" | "placeholder"> {
  label?: TranslationKeys;
  hint?: TranslationKeys;
  error?: TranslationKeys;
  /** Placeholder text — pass a translation key; the component translates it internally. */
  placeholder?: TranslationKeys;
  isRTL?: boolean;
  /** Icon rendered on the leading edge (logical start) — sits inside the input. */
  leadingIcon?: React.ReactNode;
  /** Icon rendered on the trailing edge (logical end) — sits inside the input. */
  trailingIcon?: React.ReactNode;
  /**
   * Text label or element attached to the left edge of the field, outside
   * the input but inside the border (e.g. "https://", "$", a flag emoji).
   * Renders with a subtle `bg-stroke-20` background and a divider.
   */
  leftAffix?: string | React.ReactNode;
  /**
   * Text label or element attached to the right edge of the field (e.g. ".com",
   * a currency symbol).
   */
  rightAffix?: string | React.ReactNode;
  /** Accessible label for the input (defaults to `label`). */
  accessibilityLabel?: TranslationKeys;
  /** Accessible hint for the input. */
  accessibilityHint?: TranslationKeys;
  /** Forwarded to the underlying TextInput for testing. */
  testID?: string;
  /** When true, renders a red asterisk after the label. */
  required?: boolean;
  containerClassName?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const AppTextInput = memo(function AppTextInput({
  label,
  hint,
  error,
  isRTL,
  editable = true,
  leadingIcon,
  trailingIcon,
  leftAffix,
  rightAffix,
  placeholder,
  accessibilityLabel,
  accessibilityHint,
  testID,
  required,
  containerClassName,
  ...rest
}: AppTextInputProps) {
  const { t, isRTL: contextIsRTL } = useI18n();
  const resolvedIsRTL = isRTL ?? contextIsRTL;
  const hasLeading = !!leadingIcon;
  const hasTrailing = !!trailingIcon;
  const hasLeftAffix = leftAffix != null;
  const hasRightAffix = rightAffix != null;
  const a11yLabel = accessibilityLabel ? t(accessibilityLabel) : label ? t(label) : undefined;
  const resolvedPlaceholder = placeholder ? t(placeholder) : undefined;

  const borderClass = error ? "border-error" : editable ? "border-stroke-50" : "border-stroke-20";
  const bgClass = editable ? "bg-white" : "bg-stroke-20";
  const textClass = editable ? "text-dark-text-50" : "text-dark-text-15";

  return (
    <View className={cn("mb-4 w-full items-start", containerClassName)}>
      {!!label && <Label label={label} required={required} />}

      {/* Container — overflow-hidden clips affixes to the rounded corners */}
      <View
        className={`w-full flex-row items-stretch border rounded-md overflow-hidden ${borderClass}`}
      >
        {/* Left affix */}
        {hasLeftAffix && <AffixBox side="left">{leftAffix}</AffixBox>}

        {/* Leading icon */}
        {hasLeading && (
          <View className={`ps-3 pe-1 items-center justify-center ${bgClass}`}>{leadingIcon}</View>
        )}

        {/* Text input */}
        <RNTextInput
          editable={editable}
          textAlign={resolvedIsRTL ? "right" : "left"}
          testID={testID}
          placeholder={resolvedPlaceholder}
          accessibilityLabel={a11yLabel}
          accessibilityHint={accessibilityHint ? t(accessibilityHint) : undefined}
          className={cn(
            "flex-1 py-3 text-base leading-tight",
            resolvedIsRTL && !isRTL ? "text-end" : "text-start",
            textClass,
            bgClass,
            hasLeading || hasLeftAffix ? "ps-2" : "ps-4",
            hasTrailing || hasRightAffix ? "pe-2" : "pe-4"
          )}
          {...rest}
        />

        {/* Trailing icon */}
        {hasTrailing && (
          <View className={`pe-3 ps-1 items-center justify-center ${bgClass}`}>{trailingIcon}</View>
        )}

        {/* Right affix */}
        {hasRightAffix && <AffixBox side="right">{rightAffix}</AffixBox>}
      </View>

      {!!hint && !error && (
        <AppText weight="medium" className="text-xs text-dark-text-10 mt-1 text-start">
          {t(hint)}
        </AppText>
      )}
      {!!error && (
        <AppText role="alert" className="text-xs text-error mt-1 text-start">
          {t(error)}
        </AppText>
      )}
    </View>
  );
});
